/* Curation's side panel in ONAM's style (FEATURES.curationSkin; NewSideBar renders it in place of MAP's).

   Laid out like Engage › Advertising: where you are (Discover · Curation), then the federation you are working in
   with its pages beneath it, then the pages that work across federations. A federation's pages stay listed while
   none is open (shown unavailable, with a hint), so the panel keeps its shape when you go back to choose one.
   Which pages show, and who can open them, is decided by MAP's SidebarContext exactly as before. */

import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSidebar } from "../../components/NewSideBar/SidebarContext";
import { useFederation } from "../../components/FederationSelection/FederationContext";
import { Glyph } from "../mark";
import { FED_EVENT } from "./OnamHeader";

type Item = { id: string; label: string; value: string; urls: string[] };

const GLYPH: Record<string, string> = {
  federation: "hub",
  campaign_planning: "sliders",
  campaign_reports: "chart",
  provenance_trail: "trail",
  campaign_history: "clock",
  datamanager: "layers",
  activation_connectors: "external",
  management_dashboard: "grid",
  organization_dashboard: "building",
  system_health: "activity",
};

/** Pages reached from a section that belong to it in the panel (e.g. a new query is part of Audience Curation). */
const ALSO: Record<string, string[]> = {
  campaign_planning: ["/new-query", "/ask-tamarai"],
  datamanager: ["/create-dataset"],
};

const under = (path: string, url: string) => path === url || path.startsWith(url + "/");

export default function CurationSide() {
  const history = useHistory();
  const { pathname } = useLocation();
  const { curationNav, activeSubItem, setActiveSubItem, isSideBarMenuDisabled } = useSidebar() as any;
  const { platformLogo } = useFederation();
  const [fedName, setFedName] = useState(localStorage.getItem("federationname") || "");
  useEffect(() => { setFedName(localStorage.getItem("federationname") || ""); }, [pathname]);
  useEffect(() => {
    const f = () => setFedName(localStorage.getItem("federationname") || "");
    window.addEventListener(FED_EVENT, f); return () => window.removeEventListener(FED_EVENT, f);
  }, []);

  const fedItems: Item[] = curationNav?.federation || [];
  const globalItems: Item[] = curationNav?.global || [];
  const fedOpen: boolean = !!curationNav?.fedOpen;
  const all = [...fedItems, ...globalItems];
  // the page you are on: by address first, by MAP's active label only when no address matches
  const byPath = all.find((i) => [...(i.urls || []), ...(ALSO[i.value] || [])].some((u) => under(pathname.toLowerCase(), u)));
  const isCurrent = (i: Item) => (byPath ? byPath === i : activeSubItem === i.label);

  const open = (i: Item) => {
    if (!i.urls?.[0] || isSideBarMenuDisabled?.[i.id]) return;
    setActiveSubItem(i.label);
    history.push(i.urls[0]);
  };
  const fedEntry = globalItems.find((i) => i.value === "federation");
  const choosing = !fedOpen;

  const tab = (i: Item, unavailable = false, shown = i.label) => {
    const off = unavailable || !!isSideBarMenuDisabled?.[i.id];
    return (
      <button key={i.id} type="button" className="tab" aria-current={!off && isCurrent(i) ? "page" : undefined} aria-disabled={off || undefined}
        title={unavailable ? "Open a federation to use this page" : undefined} onClick={() => (off ? undefined : open(i))}>
        <Glyph n={GLYPH[i.value] || "chevron"} /><span className="cs-l">{shown}</span>
      </button>
    );
  };

  return (
    <aside className="onam-app cside" aria-label="Curation">
      <div className="side-ctx">
        <nav className="label side-path" aria-label="You are in">
          <button type="button" onClick={() => history.push("/entry-hub?console=Discover")}>Discover</button> · <span>Curation</span>
        </nav>
      </div>

      <section className={`cs-fed ${choosing ? "choosing" : ""}`} aria-label="Federation">
        <div className="side-src cs-card">
          <span className="label">Federation</span>
          {choosing ? (
            <>
              <b className="side-src-n">No federation open</b>
              <span className="cs-hint">Choose one to open its audience pages.</span>
              {fedEntry && !under(pathname.toLowerCase(), "/choose-federation") ? (
                <button type="button" className="link" onClick={() => history.push("/choose-federation")}>Choose a federation</button>) : null}
            </>
          ) : (
            <>
              {platformLogo ? <span className="cs-logo"><img src={platformLogo} alt="" /></span> : null}
              <b className="side-src-n">{fedName || "This federation"}</b>
              <button type="button" className="link" onClick={() => history.push("/choose-federation")}>Change federation</button>
            </>
          )}
        </div>
        {fedItems.length ? <nav className="snav cs-tree" aria-label="Federation pages">{fedItems.map((i) => tab(i, choosing))}</nav> : null}
      </section>

      {globalItems.length ? (
        <section className="cs-global" aria-label="Across federations">
          <span className="label cs-gl">Across federations</span>
          <nav className="snav">{globalItems.map((i) => tab(i, false, i.value === "federation" && i.label === "Federation" ? "Federations" : i.label))}</nav>
        </section>
      ) : null}
    </aside>
  );
}
